import { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { NotificationService, NOTIFICATION_TYPE_SUCCESS, NOTIFICATION_DURATIONS } from 'ui-components/services/notification-service';

import { ENHANCED_RETURNS_MARKETPLACE_IDS } from './constants/enhanced-returns-constants';
import { MARKETPLACE_IDS_HIDDEN_FROM_RETURNS } from '../../../../../enums/marketplaces-hidden-from-returns';
import { MARKETPLACE_IDS_WITH_OWN_RETURNS } from '../../../../../enums/marketplaces-with-own-returns';
import { useFTUWizardContext } from './ftu-wizard-context';
import { getCarrierIdFromService } from './returns-helpers';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { MarketplaceIds } from '../../../../../enums/marketplace-ids';
import { SellerSettings } from '../../../../../enums/seller-settings/seller-settings';
import { usePusherEvent } from '../../../../../hooks/use-pusher-event';
import { useSellerSetting } from '../../../../../hooks/use-seller-setting';
import { LogService } from '../../../../../services/log-service';
import { PackageService } from '../../../../../services/package-service';
import { RECEIVED_USER_TOKEN_WEBHOOK_RESPONSE } from '../../../../../services/pusher-service/pusher-contract-messages';
import Global from '../../../../../store/global-selectors';
import { activateEnhancedReturns, getRetailerConfiguration } from '../../../../../store/returns/returns.api';
import { useEnhancedReturnsAccountEnabled, useIsEnhancedReturnsFeatureEnabled } from '../../../../../store/returns/returns.helpers';
import { saveNewSellerSettings, refreshAllStores, refreshStoreList } from '../../../../../store/seller/seller.actions';
import { updateSellerSettingsBulk } from '../../../../settings/api/save-settings';
import { getStore } from '../../../../settings/api/store-api';
import { ENHANCED_RETURNS_MODAL_PATH } from '../../../../settings/enhanced-returns/activation-modal/modal/enhanced-returns-activation-step-detective';
import { toggleBrandedReturns } from '../api/branded-returns-api';
import { RETURNS_YES, RETURNS_INITIATION_OPTIONS } from '../steps/returns-step/constants';
import { messages } from '../steps/returns-step/messages';

// Testing configuration - set to true to include manual stores in testing
const SHOW_MANUAL_STORE_FOR_TESTING = false;

const ReturnsContext = createContext();

export const ReturnsProvider = ({ children }) => {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { merchantSupportsReturns, updateReturnsSelection, persistedInitiationOptions, updateInitiationOptions } = useFTUWizardContext();
  const hasEnhancedReturnsAccount = useEnhancedReturnsAccountEnabled();
  const isEnhancedReturnsFeatureEnabled = useIsEnhancedReturnsFeatureEnabled();
  const existingReturnService = useSellerSetting(SellerSettings.returnService);
  const existingReturnPackage = useSellerSetting(SellerSettings.returnPackage);
  const existingReturnTracking = useSellerSetting(SellerSettings.returnTracking);
  const carriersFromSelector = useSelector(Global.selectReferenceDatagetSellerCarriers);
  const rawServices = useSelector(Global.selectSellerServices);
  const rawPackages = useSelector(Global.selectSellerPackages);
  const marketplacesReferenceFromSelector = useSelector(Global.selectReferenceDatagetMarketplaces);
  const [localSelectedReturnsOption, setLocalSelectedReturnsOption] = useState(() => merchantSupportsReturns || '');
  const [hasInitialized, setHasInitialized] = useState(false);

  if (!hasInitialized && merchantSupportsReturns) {
    setLocalSelectedReturnsOption(merchantSupportsReturns);
    setHasInitialized(true);
  }
  const selectedReturnsOption = localSelectedReturnsOption;

  const setSelectedReturnsOption = useCallback(
    (value) => {
      setLocalSelectedReturnsOption(value);
      // Persist asynchronously without blocking the UI
      Promise.resolve().then(() => {
        updateReturnsSelection(value);
      });
    },
    [updateReturnsSelection],
  );
  const [selectedServiceId, setSelectedServiceId] = useState(existingReturnService || '');
  const [selectedPackageId, setSelectedPackageId] = useState(
    () => PackageService.parseInformationFromCompositePackageId(existingReturnPackage).packageTypeId,
  );
  const [autoMarkReturnsReceived, setAutoMarkReturnsReceived] = useState(existingReturnTracking === 'true');
  const [selectedCarrierId, setSelectedCarrierId] = useState(() => getCarrierIdFromService(existingReturnService, rawServices));
  const [shouldShowReturnsSupportOptions, setShouldShowReturnsSupportOptions] = useState(true);

  const defaultInitiationOptions = {
    [RETURNS_INITIATION_OPTIONS.SELF_SERVICE_PORTAL]: false,
    [RETURNS_INITIATION_OPTIONS.MANUAL_RETURNS]: false,
  };

  const [selectedInitiationOptions, setSelectedInitiationOptions] = useState(() => persistedInitiationOptions || defaultInitiationOptions);
  const [hasInitiationOptionsInitialized, setHasInitiationOptionsInitialized] = useState(false);
  if (!hasInitiationOptionsInitialized && persistedInitiationOptions) {
    setSelectedInitiationOptions(persistedInitiationOptions);
    setHasInitiationOptionsInitialized(true);
  }

  const toggleInitiationOption = useCallback(
    (option) => {
      setSelectedInitiationOptions((prev) => {
        const newOptions = {
          ...prev,
          [option]: !prev[option],
        };
        Promise.resolve().then(() => {
          updateInitiationOptions(newOptions);
        });
        return newOptions;
      });
    },
    [updateInitiationOptions],
  );

  const hasAnyInitiationOptionSelected = useMemo(() => {
    return Object.values(selectedInitiationOptions).some((value) => value);
  }, [selectedInitiationOptions]);

  const [isEditMode, setIsEditMode] = useAsyncSafeState(false);
  const [isSaving, setIsSaving] = useAsyncSafeState(false);
  const [hasSaveError, setHasSaveError] = useAsyncSafeState(null);
  const [canActivateEnhancedReturns, setCanActivateEnhancedReturns] = useState(false);
  const [isActivatingEnhancedReturns, setIsActivatingEnhancedReturns] = useState(false);
  const [enhancedPortalUrls, setEnhancedPortalUrls] = useState(emptyObject);
  const [detailedBrandedReturnsFlags, setDetailedBrandedReturnsFlags] = useState(emptyObject);

  const hasExistingReturnSettings = useMemo(() => {
    return !!(existingReturnService && existingReturnPackage);
  }, [existingReturnService, existingReturnPackage]);

  const carriers = carriersFromSelector || emptyArray;
  const marketplacesReference = marketplacesReferenceFromSelector || emptyArray;
  const allStoresFromSelector = useSelector(Global.selectSellerStores);
  const activeStores = useMemo(() => {
    const allStores = allStoresFromSelector || [];
    return allStores.filter((store) => store.isActive && !MARKETPLACE_IDS_HIDDEN_FROM_RETURNS.includes(store.marketplaceId));
  }, [allStoresFromSelector]);

  const marketplaceStores = useMemo(() => {
    return activeStores.filter((store) => !store.isManualStore);
  }, [activeStores]);

  const manualStores = useMemo(() => {
    return activeStores.filter((store) => store.isManualStore);
  }, [activeStores]);

  const enhancedReturnsStores = useMemo(() => {
    const storesToConsider = SHOW_MANUAL_STORE_FOR_TESTING ? activeStores : marketplaceStores;
    return storesToConsider.filter(
      (store) =>
        ENHANCED_RETURNS_MARKETPLACE_IDS.includes(store.marketplaceId) && store.supportsEnhancedReturns && isEnhancedReturnsFeatureEnabled,
    );
  }, [activeStores, marketplaceStores, isEnhancedReturnsFeatureEnabled]);

  const legacyReturnsStores = useMemo(() => {
    const storesToConsider = SHOW_MANUAL_STORE_FOR_TESTING ? activeStores : marketplaceStores;
    return storesToConsider.filter((store) => {
      if (!isEnhancedReturnsFeatureEnabled) return true;

      return !ENHANCED_RETURNS_MARKETPLACE_IDS.includes(store.marketplaceId) || !store.supportsEnhancedReturns;
    });
  }, [activeStores, marketplaceStores, isEnhancedReturnsFeatureEnabled]);

  const allFlatStores = useMemo(
    () => [...enhancedReturnsStores, ...legacyReturnsStores, ...manualStores],
    [enhancedReturnsStores, legacyReturnsStores, manualStores],
  );

  const [storeToggleOverrides, setStoreToggleOverrides] = useState(emptyObject);

  const isStoreToggled = useCallback(
    (store) => {
      if (storeToggleOverrides[store.storeGuid] !== undefined) {
        return storeToggleOverrides[store.storeGuid];
      }
      if (MARKETPLACE_IDS_WITH_OWN_RETURNS.includes(store.marketplaceId)) {
        return false;
      }
      const hasBrandedReturns = detailedBrandedReturnsFlags[store.storeGuid] ?? store.useBrandedReturns;
      if (hasBrandedReturns) {
        return true;
      }
      return false;
    },
    [storeToggleOverrides, detailedBrandedReturnsFlags],
  );

  const hasMarketplaceOwnReturns = useCallback((store) => {
    return MARKETPLACE_IDS_WITH_OWN_RETURNS.includes(store.marketplaceId);
  }, []);

  const revertStoreToggle = useCallback((storeGuid) => {
    setStoreToggleOverrides((prev) => {
      const next = { ...prev };
      delete next[storeGuid];
      return next;
    });
  }, []);

  const storesByMarketplace = useMemo(() => {
    return activeStores.reduce((acc, store) => {
      const { marketplaceId } = store;
      if (!acc[marketplaceId]) {
        return { ...acc, [marketplaceId]: [store] };
      }
      return { ...acc, [marketplaceId]: [...acc[marketplaceId], store] };
    }, {});
  }, [activeStores]);
  const allServices = rawServices || emptyArray;
  const packages = rawPackages || emptyArray;

  const availableServices = useMemo(() => {
    if (!selectedCarrierId) return [];
    return allServices.filter((service) => service.carrierId === selectedCarrierId && !service.isHidden);
  }, [allServices, selectedCarrierId]);

  const availablePackages = useMemo(() => {
    if (!selectedServiceId) return [];
    const service = allServices.find((s) => s.serviceId === selectedServiceId);
    if (!service) return [];
    return packages.filter((pkg) => pkg.carrierId === service.carrierId && !pkg.isHidden);
  }, [packages, selectedServiceId, allServices]);

  const selectedCarrier = useMemo(() => {
    return carriers.find((carrier) => carrier.carrierId === selectedCarrierId);
  }, [carriers, selectedCarrierId]);

  const selectedService = useMemo(() => {
    return availableServices.find((service) => service.serviceId === selectedServiceId);
  }, [availableServices, selectedServiceId]);

  const selectedPackage = useMemo(() => {
    return availablePackages.find((pkg) => pkg.packageTypeId === selectedPackageId);
  }, [availablePackages, selectedPackageId]);

  const merchantHandlesReturns = useMemo(() => {
    return selectedReturnsOption?.toString().toLowerCase() === RETURNS_YES;
  }, [selectedReturnsOption]);

  // User Scenario Detection
  const hasNoStores = useMemo(() => {
    return activeStores.length === 0;
  }, [activeStores]);

  // Store counts helper
  const storeCounts = useMemo(
    () => ({
      total: activeStores.length,
      enhanced: enhancedReturnsStores.length,
      legacy: legacyReturnsStores.length,
      activeManualStores: activeStores.filter((store) => store.isManualStore).length,
      shopifyStores: storesByMarketplace[MarketplaceIds.Shopify]?.length || 0,
      bigCommerceStores: storesByMarketplace[MarketplaceIds.BigCommerce]?.length || 0,
      wooCommerceStores: storesByMarketplace[MarketplaceIds.WooCommerce]?.length || 0,
    }),
    [activeStores, enhancedReturnsStores, legacyReturnsStores, storesByMarketplace],
  );

  // Add store activation status tracking
  const storeActivationStatus = useMemo(() => {
    return enhancedReturnsStores.reduce(
      (acc, store) => ({
        ...acc,
        [store.id]: {
          isEligible: store.supportsEnhancedReturns,
          isActivated: store.isStoreSetupCompleteForEnhancedReturns,
          canActivate: store.supportsEnhancedReturns && !store.isStoreSetupCompleteForEnhancedReturns,
          marketplaceName: store.marketplaceName || 'Unknown',
          storeName: store.name || 'Unnamed Store',
        },
      }),
      {},
    );
  }, [enhancedReturnsStores]);

  const handleActivateEnhancedReturns = useCallback(
    (storeGuid) => {
      const redirectUrl = location.pathname;
      history.push(ENHANCED_RETURNS_MODAL_PATH, { redirectUrl, storeGuid });
    },
    [history, location.pathname],
  );

  const handleCarrierChange = useCallback((carrierId) => {
    setSelectedCarrierId(carrierId);
    setSelectedServiceId('');
    setSelectedPackageId('');
  }, []);

  const handleServiceChange = useCallback(
    (serviceId) => {
      setSelectedServiceId(serviceId);
      setSelectedPackageId('');
      // Update carrier based on selected service
      const service = allServices.find((s) => s.serviceId === serviceId);
      if (service) {
        setSelectedCarrierId(service.carrierId);
      }
    },
    [allServices],
  );

  const handleSaveReturnService = useCallback(async () => {
    if (!selectedServiceId || !selectedPackage?.packageTypeId) {
      setHasSaveError(intl.formatMessage(messages.selectServiceAndPackageError));
      return;
    }

    setIsSaving(true);
    setHasSaveError(null);

    const settingsToSave = {
      [SellerSettings.returnService.key]: selectedServiceId,
      [SellerSettings.returnPackage.key]:
        `${selectedPackage?.packageTypeId}-${selectedPackage?.isCustom}-${selectedCarrier?.carrierId || ''}`,
      [SellerSettings.returnTracking.key]: autoMarkReturnsReceived.toString(),
    };

    try {
      const newSettings = await updateSellerSettingsBulk(settingsToSave);
      dispatch(saveNewSellerSettings(newSettings));
      setIsEditMode(false);

      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_SUCCESS,
        content: intl.formatMessage(messages.returnSettingsSavedSuccess),
        duration: NOTIFICATION_DURATIONS.SHORT,
      });
    } catch (error) {
      setHasSaveError(intl.formatMessage(messages.returnSettingsSaveError));
      LogService.logError(error);
    } finally {
      setIsSaving(false);
    }
  }, [
    selectedServiceId,
    selectedPackage,
    selectedCarrier,
    autoMarkReturnsReceived,
    dispatch,
    intl,
    setHasSaveError,
    setIsEditMode,
    setIsSaving,
  ]);

  const handleEditReturnSettings = useCallback(() => {
    setIsEditMode(true);
    setHasSaveError(null);
  }, [setIsEditMode, setHasSaveError]);

  const handleCancelEdit = useCallback(() => {
    setIsEditMode(false);
    setHasSaveError(null);
    if (existingReturnService) {
      setSelectedServiceId(existingReturnService);
    }
    if (existingReturnPackage) {
      setSelectedPackageId(PackageService.parseInformationFromCompositePackageId(existingReturnPackage).packageTypeId);
    }
    if (existingReturnTracking) {
      setAutoMarkReturnsReceived(existingReturnTracking === 'true');
    }
  }, [existingReturnService, existingReturnPackage, existingReturnTracking, setIsEditMode, setHasSaveError]);

  const refreshStores = useCallback(() => {
    dispatch(refreshAllStores('FTU_WIZARD_RETURNS'));
  }, [dispatch]);

  const refetchStores = useCallback(() => {
    dispatch(refreshStoreList());
  }, [dispatch]);

  const handleStoreToggle = useCallback(
    (store) => {
      const isCurrentlyToggled = isStoreToggled(store);
      const newValue = !isCurrentlyToggled;
      setStoreToggleOverrides((prev) => ({ ...prev, [store.storeGuid]: newValue }));

      const isAlreadyActivated = store.isStoreSetupCompleteForEnhancedReturns || store.useBrandedReturns;
      const shouldToggleDirectly = isAlreadyActivated || store.isManualStore;
      if (shouldToggleDirectly) {
        toggleBrandedReturns(store.storeGuid, newValue)
          .then(() => {
            setDetailedBrandedReturnsFlags((prev) => ({ ...prev, [store.storeGuid]: newValue }));
            refreshStores();
          })
          .catch((error) => {
            LogService.logError(new Error('Failed to toggle branded returns for store', { cause: error }), { storeGuid: store.storeGuid });
            setStoreToggleOverrides((prev) => ({ ...prev, [store.storeGuid]: isCurrentlyToggled }));
          });
      }
    },
    [isStoreToggled, refreshStores],
  );

  const fetchEnhancedPortalUrlIfNeeded = useCallback(
    async (storeGuid) => {
      if (enhancedPortalUrls[storeGuid] !== undefined) {
        return;
      }

      try {
        const data = await getRetailerConfiguration(storeGuid);
        const portalDomains = data?.customer_portal_domains;
        if (portalDomains && portalDomains.length > 0) {
          setEnhancedPortalUrls((prev) => ({ ...prev, [storeGuid]: portalDomains[0] }));
        } else {
          setEnhancedPortalUrls((prev) => ({ ...prev, [storeGuid]: intl.formatMessage(messages.portalUrlLoadError) }));
        }
      } catch (error) {
        LogService.logError(new Error('Failed to fetch enhanced returns portal URL', { cause: error }), { storeGuid });
        setEnhancedPortalUrls((prev) => ({ ...prev, [storeGuid]: intl.formatMessage(messages.portalUrlLoadError) }));
      }
    },
    [enhancedPortalUrls, intl],
  );

  useEffect(() => {
    const storesToCheck = allFlatStores.filter(
      (store) => store.useBrandedReturns && detailedBrandedReturnsFlags[store.storeGuid] === undefined,
    );
    if (storesToCheck.length === 0) return;

    storesToCheck.forEach(async (store) => {
      try {
        const detailedStore = await getStore(store.storeGuid);
        const isActuallyEnabled = !!detailedStore?.returns?.useBrandedReturns;
        setDetailedBrandedReturnsFlags((prev) => ({ ...prev, [store.storeGuid]: isActuallyEnabled }));
      } catch (error) {
        LogService.logError(new Error('Failed to fetch detailed store data', { cause: error }), { storeGuid: store.storeGuid });
      }
    });
  }, [allFlatStores, detailedBrandedReturnsFlags]);

  useEffect(() => {
    const storesToFetch = allFlatStores.filter((store) => store.isStoreSetupCompleteForEnhancedReturns || store.useBrandedReturns);
    storesToFetch.forEach((store) => {
      fetchEnhancedPortalUrlIfNeeded(store.storeGuid);
    });
  }, [allFlatStores, fetchEnhancedPortalUrlIfNeeded]);

  useEffect(() => {
    if (!hasEnhancedReturnsAccount && enhancedReturnsStores.length > 0 && isEnhancedReturnsFeatureEnabled) {
      setIsActivatingEnhancedReturns(true);

      const activateReturns = async () => {
        try {
          await activateEnhancedReturns();
          LogService.logInfo('Enhanced returns activation initiated successfully');
        } catch (error) {
          LogService.logError(new Error('Failed to activate enhanced returns', { cause: error }));
          setIsActivatingEnhancedReturns(false);
        }
      };

      activateReturns();
    }
  }, [hasEnhancedReturnsAccount, enhancedReturnsStores.length, isEnhancedReturnsFeatureEnabled]);

  const onEnhancedReturnsActivationComplete = useCallback(() => {
    if (isActivatingEnhancedReturns) {
      setCanActivateEnhancedReturns(true);
      setIsActivatingEnhancedReturns(false);
      LogService.logInfo('Enhanced returns activation completed via Pusher event');
      refreshStores();
    }
  }, [isActivatingEnhancedReturns, refreshStores]);

  usePusherEvent(RECEIVED_USER_TOKEN_WEBHOOK_RESPONSE, onEnhancedReturnsActivationComplete);

  const value = {
    connectedStores: activeStores,
    enhancedReturnsStores,
    legacyReturnsStores,
    storesByMarketplace,
    hasNoStores,
    storeCounts,
    storeActivationStatus,
    handleActivateEnhancedReturns,
    marketplacesReference,
    carriers,
    allServices,
    packages,
    selectedReturnsOption,
    selectedCarrierId,
    selectedServiceId,
    selectedPackageId,
    shouldShowReturnsSupportOptions,
    autoMarkReturnsReceived,
    availableServices,
    availablePackages,
    selectedCarrier,
    selectedService,
    selectedPackage,
    merchantHandlesReturns,
    setSelectedReturnsOption,
    setSelectedCarrierId: handleCarrierChange,
    setSelectedServiceId: handleServiceChange,
    setSelectedPackageId,
    setShouldShowReturnsSupportOptions,
    setAutoMarkReturnsReceived,
    handleSaveReturnService,
    hasExistingReturnSettings,
    isEditMode,
    isSaving,
    hasSaveError,
    handleEditReturnSettings,
    handleCancelEdit,
    refreshStores,
    refetchStores,
    manualStores,
    marketplaceStores,
    hasOnlyManualStores: marketplaceStores.length === 0 && manualStores.length > 0,
    showManualStoreForTesting: SHOW_MANUAL_STORE_FOR_TESTING,
    hasEnhancedReturnsAccount,
    canActivateEnhancedReturns,
    isActivatingEnhancedReturns,
    enhancedPortalUrls,
    selectedInitiationOptions,
    toggleInitiationOption,
    hasAnyInitiationOptionSelected,
    allFlatStores,
    isStoreToggled,
    handleStoreToggle,
    hasMarketplaceOwnReturns,
    revertStoreToggle,
  };

  return <ReturnsContext.Provider value={value}>{children}</ReturnsContext.Provider>;
};

export const useReturnsContext = () => {
  const context = useContext(ReturnsContext);
  if (context === undefined) {
    throw new Error('useReturnsContext must be used within a ReturnsProvider');
  }
  return context;
};
